import React, { useEffect, useState } from 'react';
import { Button, Grid, Box, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import PageWrapper from 'components/Common/PageWraper';
import PageHeader from 'components/Header/Page';
import { FiltersCard, AdvancedFiltersCard } from '../PanelFilters';
import { FilterConfigSection, FilterValues } from 'components/Dropdown/MultiFilterPanel/type';
import MultiFilterPanel from 'components/Dropdown/MultiFilterPanel';
import { useHeader } from 'Context/contexts/HeaderTabRightContentContext';
import dayjs, { Dayjs } from 'dayjs';
import GenericDateRangeFilter from 'components/Dropdown/GenericDateRangeFilter';
import GenericMonthRangeFilter from 'components/Dropdown/GenericMonthRangeFilter';
import GenericMonthFilter from 'components/Dropdown/GenericMonthFilter';
import SingleSelectFilter from 'components/Dropdown/SingleSelectFilter';

export default function FiltersDemoPage() {
  const { setRightContextContent } = useHeader();
  const navigate = useNavigate();
  const [applied, setApplied] = useState<FilterValues>({});

  const [startDate, setStartDate] = useState<Dayjs | null>(null);
  const [endDate, setEndDate] = useState<Dayjs | null>(null);
  const [selectedMonth, setSelectedMonth] = useState<string>('2026-04');
  const [filterMonth, setFilterMonth] = useState<string>('0M');
  const [customMonth, setCustomMonth] = useState<string>('');
  const [demoSectorName, setDemoSectorName] = useState('all');

  const handleMonthRangeChange = (selectedDate: string, isCustomDate?: boolean) => {
    if (isCustomDate) {
      setCustomMonth(selectedDate);
    } else {
      setCustomMonth('');
    }

    setFilterMonth(selectedDate);
  };

  const demoSectorOptionsList = [
    'Finance',
    'Marketing',
    'Operations',
    'Technology',
    {
      name: 'Human Resources',
      isAllowed: true
    },
    {
      name: 'Restricted Sector',
      isAllowed: false
    },
    {
      label: 'Cloud Services',
      value: 'cloud-services'
    }
  ];

  const handleDemoSectorChange = (value: string) => {
    console.log('Selected sector:', value);
  };

  useEffect(() => {
    setRightContextContent(
      <Box display="flex" alignItems="center" gap={2}>
        <GenericMonthFilter
          selectedMonth={selectedMonth}
          minDate={dayjs('2024-01-01')}
          maxDate={dayjs()}
          onChange={(date) => setSelectedMonth(date)}
        />

        <GenericDateRangeFilter
          startDate={startDate}
          endDate={endDate}
          // minDate={dayjs('2024-01-01')} // optional: earliest selectable date
          // maxDate={dayjs('2026-02-28')} // optional: latest selectable date (future dates are blocked beyond today)
          onChange={(range: any) => {
            setStartDate(range.startDate);
            setEndDate(range.endDate);
          }}
        />

        <GenericMonthRangeFilter
          customMonth={customMonth}
          filterMonth={filterMonth}
          setCustomMonth={setCustomMonth}
          setFilterMonth={setFilterMonth}
          handleChange={handleMonthRangeChange}
        />

        <MultiFilterPanel
          sections={filterSections}
          appliedValues={applied}
          setAppliedValues={setApplied}
          onApplyFilters={(values: any, meta: any) => {
            setApplied(values);
            console.log('Applied Values:', values);
            console.log('Meta:', meta);
          }}
          popupWidth={360}
          maxHeight={520}
          clearText="CLEAR FILTER"
          clearColor="#FF8A00"
        />

        <SingleSelectFilter
          sectorName={demoSectorName}
          setSectorName={setDemoSectorName}
          sectorOptionsList={demoSectorOptionsList}
          handleChange={handleDemoSectorChange}
          hasNotProvided
        />
      </Box>,
    );

    return () => {
      setRightContextContent(null);
    };
  }, [applied, startDate, endDate, filterMonth, selectedMonth, customMonth]);

  useEffect(() => {
    console.log('selected month:', selectedMonth);
  }, [selectedMonth]);

  const filterSections: FilterConfigSection[] = [
    {
      items: [
        {
          key: 'businessUnit',
          label: 'Business Unit',
          type: 'checkbox_group',
          options: [
            { label: 'All', value: 'all' },
            { label: 'Foods', value: 'foods' },
            { label: 'Beverages', value: 'beverages' },
          ],
        },
        {
          key: 'region',
          label: 'Region',
          type: 'checkbox_group',
          options: [
            { label: 'All', value: 'all' },
            { label: 'North America', value: 'north_america' },
            { label: 'International', value: 'international' },
          ],
        },
      ],
    },
    {
      title: 'Additional Filters',
      items: [
        {
          key: 'sectors',
          label: 'Sectors',
          type: 'dropdown_multi',
          showSearch: true,
          showAllOption: true,
          placeholder: 'All Sectors',
          chipDisplay: true,
          options: [
            { label: 'PBNA', value: 'pbna' },
            { label: 'PFNA', value: 'pfna' },
            { label: 'AMESA', value: 'amesa' },
            { label: 'APAC', value: 'apac' },
            { label: 'LATAM', value: 'latam' },
            { label: 'EUROPE', value: 'europe' },
          ],
        },
        {
          key: 'businessDomain',
          label: 'Business Domain',
          type: 'dropdown_multi',
          showSearch: true,
          showAllOption: true,
          placeholder: 'All Business Domain',
          options: [
            { label: 'FinOps', value: 'finops' },
            { label: 'DataOps', value: 'dataops' },
            { label: 'SecOps', value: 'secops' },
          ],
        },
        {
          key: 'platform',
          label: 'Platform',
          type: 'dropdown_multi',
          showSearch: true,
          showAllOption: true,
          placeholder: 'All Platforms',
          options: [
            { label: 'Azure', value: 'azure' },
            { label: 'GCP', value: 'gcp' },
            { label: 'AWS', value: 'aws' },
          ],
        },
        {
          key: 'programs',
          label: 'Programs',
          type: 'dropdown_multi',
          showSearch: true,
          showAllOption: true,
          placeholder: 'All Programs',
          chipDisplay: true, // show chips like Program 1/2/3
          options: [
            { label: 'Program 1', value: 'program_1' },
            { label: 'Program 2', value: 'program_2' },
            { label: 'Program 3', value: 'program_3' },
            { label: 'Program 4', value: 'program_4' },
          ],
        },
        {
          key: 'department',
          label: 'Department',
          type: 'dropdown_multi',
          showSearch: true,
          showAllOption: true,
          placeholder: 'All Departments',
          chipDisplay: true,
          options: [
            { label: 'Engineering', value: 'engineering' },
            { label: 'Finance', value: 'finance' },
            { label: 'HR', value: 'hr' },
          ],
        },
      ],
    },
  ];

  return (
    <PageWrapper>
      <PageHeader title="Filters Demo">
        <Button variant="pepwiseSecondary" onClick={() => navigate(-1)}>
          ← Back
        </Button>
      </PageHeader>

      <Box mt={3}>
        <Box sx={{ p: 3 }}>
          <Typography variant="h6" color={'#000'}>
            Panel Filter Applied values
          </Typography>
          {/* Example: show applied payload in Multi panel filter */}
          <pre
            style={{
              marginTop: 24,
              fontSize: 12,
              color: '#000',
              background: '#fff',
              padding: '12px',
              border: '1px solid rgba(0,0,0,0.08)',
              borderRadius: '4px',
              overflow: 'auto',
            }}
          >
            {JSON.stringify(applied, null, 2)}
          </pre>
        </Box>

        <Grid container spacing={4}>
          <Grid item xs={12}>
            <FiltersCard />
          </Grid>

          <Grid item xs={12}>
            <AdvancedFiltersCard
              filterOptions={{
                Domain: ['Global', 'APAC', 'Europe'],
                Program: ['Program A', 'Program B'],
                Department: ['IT', 'Finance', 'HR'],
                Capability: ['Monitoring', 'Automation'],
                Sector: ['Sector 1', 'Sector 2'],
              }}
              initialFilters={{
                Domain: ['Global'],
                Program: [],
                Department: [],
                Capability: [],
                Sector: [],
              }}
              onApply={(filters) => {
                console.log('Applied filters:', filters);
              }}
              onClear={() => {
                console.log('Filters cleared');
              }}
            />
          </Grid>
        </Grid>
      </Box>
    </PageWrapper>
  );
}
